/**
 * Channel-switch latency profile:
 * `localStorage.setItem('ano-cs-profile', '1')` then reload.
 *
 * Emits ONE line per channel switch with three timing buckets so we
 * can tell where the latency went without re-running the rest of the
 * debug stream. See `apps/desktop/src/lib/channel-switch-profile.ts`
 * for the breakdown of stages (`click → emit → paint → settled`).
 *
 * Sample output:
 *   [cs] hiring/abc123 click→emit=85ms emit→paint=120ms paint→settled=42ms total=247ms msgs=50
 */
export const CHANNEL_SWITCH_PROFILE =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-cs-profile") === "1";

/**
 * Scroll-experience profile:
 * `localStorage.setItem('ano-scroll-profile', '1')` then reload.
 *
 * Emits `[sc] ...` lines for the load-more decisions and window-bounds
 * commits that happen while the user is scrolling — shift-up /
 * shift-down dispatches, Zero limit bumps, the post-commit anchor-
 * restore math in `MessageScrollContainer.cdu`, and periodic scrollTop
 * samples. Pair with `[lt]` long-task lines (same observer as
 * ano-cs-profile) to attribute visible hitches to a specific commit.
 *
 * See `apps/desktop/src/lib/scroll-profile.ts`.
 */
export const SCROLL_PROFILE =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-scroll-profile") === "1";

/**
 * Singleton MessageActions (the hover pill). Replaces the per-row
 * `<MessageActions />` with ONE instance positioned over the hovered
 * row. Enable with `localStorage.setItem('ano-singleton-message-actions', '1')`.
 *
 * Why: every row mounts ~7 Radix `<Tooltip>` instances + a
 * `<TooltipProvider>` of its own. A 200-row shift-up = 1400 simultaneous
 * Radix mounts → 450ms phase-2 hitch. The singleton mounts once and
 * teleports between rows. See `HoveredMessageActions.tsx`.
 */
export const SINGLETON_MESSAGE_ACTIONS =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-singleton-message-actions") === "1";

/** Enable render diagnostics: localStorage.setItem('ano-render-debug', '1') */
export const RENDER_DEBUG =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-render-debug") === "1";

/** Enable perf diagnostics: localStorage.setItem('ano-perf-debug', '1') */
export const PERF_DEBUG =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-perf-debug") === "1";

/**
 * Enable multiplex diagnostics: localStorage.setItem('ano-multiplex-debug', '1').
 * Scoped to MessageArea MOUNT / UNMOUNT / ACTIVE / HIDDEN lines — lets you
 * verify the kept-alive multiplex without the full render-debug firehose.
 */
export const MULTIPLEX_DEBUG =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-multiplex-debug") === "1";

/**
 * Disable channel preload entirely: localStorage.setItem('ano-disable-preload', '1').
 * Dev utility for testing the cold-channel skeleton experience — every
 * channel click becomes cold, shows skeleton until server-complete
 * fires. Remove the flag to restore normal preload behavior.
 */
export const DISABLE_PRELOAD =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-disable-preload") === "1";

/**
 * Diagnose "viewport jumps down on switch-back to scrolled-up channel":
 * `localStorage.setItem('ano-trace-jump', '1')`.
 *
 * Single-line `[jump]` entries through the critical channel-switch
 * pipeline — channel-switch pin decision, focus-target dispatch +
 * rAF expansion, snapshot captures (anchorId / wasNearBottom),
 * componentDidUpdate branch taken, scroll-target anchor apply,
 * isNearBottomRef flips. Designed to read top-to-bottom and tell
 * exactly which step moved the viewport.
 */
export const JUMP_TRACE =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-trace-jump") === "1";

/**
 * Diagnose "messages pop in after channel switch" symptom:
 * `localStorage.setItem('ano-debug-popin', '1')`.
 *
 * Logs a single line per emit per channel showing how the message list
 * grew between emits — first emit's count + ids, then every later emit
 * lists which ids were added/removed. Lets you see which messages were
 * "instant" (in the first emit) vs which "popped in" (added in a later
 * emit), and whether the additions are at the head (newer) or tail
 * (older) of the list.
 *
 * Read at module-load time. Reload to toggle.
 */
export const POPIN_DEBUG =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function" &&
  localStorage.getItem("ano-debug-popin") === "1";
